import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { 
  Mail, FileText, Save, TestTube, Eye, EyeOff, 
  Server, Lock, ChevronDown, ChevronUp, Check, X,
  AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/i18n/LanguageContext';

const CATEGORY_COLORS = {
  Onboarding: 'bg-blue-100 text-blue-700',
  Authentication: 'bg-purple-100 text-purple-700',
  Billing: 'bg-amber-100 text-amber-700',
  Attendance: 'bg-emerald-100 text-emerald-700',
  Leave: 'bg-indigo-100 text-indigo-700',
  Payroll: 'bg-rose-100 text-rose-700',
};

function TemplateCard({ template, onSave }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.body_html);
  const [enabled, setEnabled] = useState(template.enabled);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/superadmin/mailer/templates/${template.template_id}`, {
        subject, body_html: bodyHtml, enabled
      }, { withCredentials: true });
      toast.success('Template updated');
      setEditing(false);
      onSave();
    } catch {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (val) => {
    setEnabled(val);
    try {
      await axios.put(`${API}/superadmin/mailer/templates/${template.template_id}`, {
        enabled: val
      }, { withCredentials: true });
      toast.success(val ? 'Template enabled' : 'Template disabled');
      onSave();
    } catch {
      toast.error('Failed to update');
      setEnabled(!val);
    }
  };

  const catColor = CATEGORY_COLORS[template.category] || 'bg-gray-100 text-gray-700';

  return (
    <div className="border rounded-lg bg-white hover:shadow-sm transition-shadow" data-testid={`template-${template.template_id}`}>
      <div 
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 bg-gray-50 rounded-lg">
            <FileText className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm text-gray-900">{template.name}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${catColor}`}>{template.category}</span>
            </div>
            <p className="text-xs text-gray-500 truncate">{template.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-4">
          <Switch
            checked={enabled}
            onCheckedChange={(val) => { handleToggle(val); }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`toggle-${template.template_id}`}
          />
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t">
          <div className="mt-3 space-y-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1">Subject</Label>
              {editing ? (
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="text-sm"
                  data-testid={`subject-${template.template_id}`}
                />
              ) : (
                <p className="text-sm text-gray-800 bg-gray-50 rounded px-3 py-2">{subject}</p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1">Body (HTML)</Label>
              {editing ? (
                <textarea
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  rows={8}
                  className="w-full text-sm border rounded-md px-3 py-2 font-mono bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  data-testid={`body-${template.template_id}`}
                />
              ) : (
                <div 
                  className="text-sm bg-gray-50 rounded px-3 py-2 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1">Available Variables</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {template.variables.map((v) => (
                  <code key={v} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{`{{${v}}}`}</code>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              {editing ? (
                <>
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`save-template-${template.template_id}`}>
                    {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(false); setSubject(template.subject); setBodyHtml(template.body_html); }}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`edit-template-${template.template_id}`}>
                  Edit Template
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MailerPage() {
  const { t, isRTL } = useLanguage();
  const [templates, setTemplates] = useState([]);
  const [smtp, setSmtp] = useState({ host: '', port: 587, username: '', password: '', from_email: '', from_name: 'Trackly', encryption: 'tls' });
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [filterCategory, setFilterCategory] = useState('All');

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/mailer/templates`, { withCredentials: true });
      setTemplates(res.data.templates);
    } catch {
      toast.error('Failed to load templates');
    }
  }, []);

  const fetchSmtp = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/mailer/smtp`, { withCredentials: true });
      setSmtp(res.data.smtp);
      setSmtpConfigured(res.data.configured);
    } catch {
      toast.error('Failed to load SMTP settings');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchTemplates(), fetchSmtp()]).finally(() => setLoading(false));
  }, [fetchTemplates, fetchSmtp]);

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    if (!smtp.host || !smtp.username || !smtp.from_email) {
      toast.error('Please fill all required fields');
      return;
    }
    setSavingSmtp(true);
    try {
      await axios.put(`${API}/superadmin/mailer/smtp`, smtp, { withCredentials: true });
      toast.success('SMTP settings saved');
      setSmtpConfigured(true);
      fetchSmtp();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    try {
      const res = await axios.post(`${API}/superadmin/mailer/smtp/test`, {}, { withCredentials: true });
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTestingSmtp(false);
    }
  };

  const categories = ['All', ...new Set(templates.map(t => t.category))];
  const filtered = filterCategory === 'All' ? templates : templates.filter(t => t.category === filterCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto" data-testid="mailer-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("mailer.title")}</h1>
        <p className="text-gray-500 text-sm">Manage email templates and SMTP configuration</p>
      </div>

      <Tabs defaultValue="templates">
        <TabsList className="mb-4">
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="w-4 h-4 mr-1.5" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="smtp" data-testid="smtp-tab">
            <Server className="w-4 h-4 mr-1.5" />
            SMTP Setting
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <Button
                key={cat}
                size="sm"
                variant={filterCategory === cat ? "default" : "outline"}
                onClick={() => setFilterCategory(cat)}
                className="text-xs"
                data-testid={`filter-${cat.toLowerCase()}`}
              >
                {cat}
                {cat !== 'All' && (
                  <span className="ml-1 text-xs opacity-70">
                    ({templates.filter(t => t.category === cat).length})
                  </span>
                )}
              </Button>
            ))}
          </div>

          <div className="space-y-2" data-testid="templates-list">
            {filtered.map(t => (
              <TemplateCard key={t.template_id} template={t} onSave={fetchTemplates} />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Mail className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No templates in this category</p>
            </div>
          )}
        </TabsContent>

        {/* SMTP Tab */}
        <TabsContent value="smtp">
          <Card data-testid="smtp-settings-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Server className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">SMTP Configuration</CardTitle>
                    <CardDescription>Configure the SMTP server used for sending all emails</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {smtpConfigured ? (
                    <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <Check className="w-3 h-3" /> Configured
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
                      <AlertCircle className="w-3 h-3" /> Not Configured
                    </span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSmtp} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp-host">SMTP Host <span className="text-red-500">*</span></Label>
                    <Input
                      id="smtp-host"
                      value={smtp.host}
                      onChange={(e) => setSmtp({ ...smtp, host: e.target.value })}
                      placeholder="smtp.gmail.com"
                      data-testid="smtp-host"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp-port">Port</Label>
                    <Input
                      id="smtp-port"
                      type="number"
                      value={smtp.port}
                      onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })}
                      placeholder="587"
                      data-testid="smtp-port"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp-username">Username <span className="text-red-500">*</span></Label>
                    <Input
                      id="smtp-username"
                      value={smtp.username}
                      onChange={(e) => setSmtp({ ...smtp, username: e.target.value })}
                      placeholder="your@email.com"
                      data-testid="smtp-username"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp-password">Password <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <Input
                        id="smtp-password"
                        type={showPassword ? "text" : "password"}
                        value={smtp.password}
                        onChange={(e) => setSmtp({ ...smtp, password: e.target.value })}
                        placeholder="App password or SMTP password"
                        className="pr-10"
                        data-testid="smtp-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="smtp-from-email">From Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="smtp-from-email"
                      type="email"
                      value={smtp.from_email}
                      onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })}
                      placeholder="noreply@yourcompany.com"
                      data-testid="smtp-from-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="smtp-from-name">From Name</Label>
                    <Input
                      id="smtp-from-name"
                      value={smtp.from_name}
                      onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })}
                      placeholder="Trackly"
                      data-testid="smtp-from-name"
                    />
                  </div>
                </div>

                <div>
                  <Label>Encryption</Label>
                  <div className="flex gap-3 mt-1.5">
                    {['tls', 'ssl', 'none'].map(enc => (
                      <button
                        key={enc}
                        type="button"
                        onClick={() => setSmtp({ ...smtp, encryption: enc, port: enc === 'ssl' ? 465 : enc === 'tls' ? 587 : 25 })}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm transition-colors ${
                          smtp.encryption === enc 
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700' 
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        data-testid={`encryption-${enc}`}
                      >
                        <Lock className="w-3.5 h-3.5" />
                        {enc.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={savingSmtp} data-testid="save-smtp-btn">
                    {savingSmtp ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                    Save Settings
                  </Button>
                  {smtpConfigured && (
                    <Button type="button" variant="outline" onClick={handleTestSmtp} disabled={testingSmtp} data-testid="test-smtp-btn">
                      {testingSmtp ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <TestTube className="w-4 h-4 mr-1.5" />}
                      Test Connection
                    </Button>
                  )}
                </div>
              </form>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Common SMTP Configurations</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
                  <div className="bg-white rounded p-3 border">
                    <p className="font-medium text-gray-800 mb-1">Gmail</p>
                    <p>Host: smtp.gmail.com</p>
                    <p>Port: 587 (TLS) / 465 (SSL)</p>
                    <p>Use App Password</p>
                  </div>
                  <div className="bg-white rounded p-3 border">
                    <p className="font-medium text-gray-800 mb-1">Outlook / Office 365</p>
                    <p>Host: smtp.office365.com</p>
                    <p>Port: 587 (TLS)</p>
                  </div>
                  <div className="bg-white rounded p-3 border">
                    <p className="font-medium text-gray-800 mb-1">Custom / SendGrid</p>
                    <p>Host: smtp.sendgrid.net</p>
                    <p>Port: 587 (TLS)</p>
                    <p>Username: apikey</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
